import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { AvatarImage } from './avatar-image'
import { useVoiceCall } from '../contexts/voice-call-context'
import { useVoiceCallSession } from '../hooks/use-voice-call-session'
import { MinimizeOverlayButton } from './voice-call-overlay'
import { resolveSelfUserId } from '../services/chat-service'
import { loadDirectory, useDirectory } from '../stores/directory-store'
import { useAuth } from '../contexts/auth-context'
import { isDesktopWeb } from '../utils/platform'
import { minimizedCallOverlayClassName } from '../utils/call-overlay-layers'
import { CallAudioRouteControl } from '../features/calls/components/call-audio-route-control'
import type { ChatUser } from '../types/chat'
import type { CallParticipant } from '../types/voice-call'
import { useTileGrid } from '../features/group-call/hooks/use-tile-grid'
import { useTileView, TILE_VIEW_MODES, type TileViewMode } from '../features/group-call/hooks/use-tile-view'
import { useDraggablePip } from '../hooks/use-draggable-pip'
import { useCallConnectStatus } from '../features/calls/hooks/use-call-connect-status'
import { CallReconnectingBanner } from '../features/calls/components/call-reconnecting-banner'
import { GroupCallPrejoin } from '../features/group-call/components/group-call-prejoin'
import { useResizableSidebar } from '../features/group-call/hooks/use-resizable-sidebar'
import { useInCallChat } from '../features/group-call/hooks/use-in-call-chat'
import { useInCallChatAlert } from '../features/group-call/hooks/use-in-call-chat-alert'
import { VideoUpgradeUnavailableDialog } from '../features/calls/components/video-upgrade-unavailable-dialog'
import {
  GroupCallSidebar,
  type GroupCallSidebarTab,
} from '../features/group-call/components/group-call-sidebar'
// Background effects (None/Blur/Calm blue/Forest/Studio) are temporarily removed
// (the Chime background blur/replacement processor wasn't working reliably). The
// controller `setBackgroundFilter` + adapter processor wiring are intentionally
// left in place so this can be re-enabled later by restoring the picker UI.

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.56.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.56 1 1 0 01-.25 1.01l-2.2 2.22z" />
    </svg>
  )
}

function MicIcon({ muted }: { muted: boolean }) {
  if (muted) {
    return (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22M9 9v3a3 3 0 005.12 2.12M15 9.34V5a3 3 0 00-5.94-.6" />
        <path d="M12 19v3M8 22h8M12 15a3 3 0 01-3-3V5a3 3 0 016 0v7" />
      </svg>
    )
  }
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 1a3 3 0 00-3 3v7a3 3 0 006 0V4a3 3 0 00-3-3z" />
      <path d="M19 10v1a7 7 0 01-14 0v-1M12 19v3M8 22h8" />
    </svg>
  )
}

function CameraIcon({ off }: { off: boolean }) {
  if (off) {
    return (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22M5 7h3l2-2h4l2 2h4a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2z" />
      </svg>
    )
  }
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function VideoIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  )
}

function PersonAddIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M22 11h-6" />
    </svg>
  )
}

function ScreenShareIcon({ active }: { active: boolean }) {
  if (active) {
    return (
      <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
        <path d="M1 1l22 22" />
        <path d="M3 5.5A1.5 1.5 0 014.5 4H19M21 7v8a2 2 0 01-2 2H9M3 8v7a2 2 0 002 2h2M8 21h8M12 17v4" />
      </svg>
    )
  }
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4M9 10.5l3-2.5 3 2.5M12 8v5" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
    </svg>
  )
}

function FullscreenIcon({ active }: { active: boolean }) {
  if (active) {
    return (
      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M8 3v3a2 2 0 01-2 2H3M21 8h-3a2 2 0 01-2-2V3M3 16h3a2 2 0 012 2v3M16 21v-3a2 2 0 012-2h3" />
      </svg>
    )
  }
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M8 3H5a2 2 0 00-2 2v3M21 8V5a2 2 0 00-2-2h-3M3 16v3a2 2 0 002 2h3M16 21h3a2 2 0 002-2v-3" />
    </svg>
  )
}

const TILE_VIEW_LABELS: Record<TileViewMode, string> = {
  auto: 'Auto',
  speaker: 'Speaker',
  grid: 'Grid',
}

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) {
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

/** Binds a remote participant's Chime video tile to its own <video> element. */
function RemoteTileVideo({
  tileId,
  bind,
  unbind,
}: {
  tileId: number
  bind: (tileId: number, el: HTMLVideoElement) => void
  unbind: (tileId: number) => void
}) {
  const ref = useRef<HTMLVideoElement | null>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    bind(tileId, el)
    return () => unbind(tileId)
  }, [tileId, bind, unbind])
  return (
    <video
      ref={ref}
      className="absolute inset-0 h-full w-full bg-slate-900 object-cover"
      autoPlay
      playsInline
    />
  )
}

function PinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M16 3l5 5-2 2-1-1-4 4 1 4-2 2-4-4-5 5-1-1 5-5-4-4 2-2 4 1 4-4-1-1z" />
    </svg>
  )
}

function ParticipantTile({
  participant,
  isSelf,
  bind,
  unbind,
  registerSelfVideo,
  selfCameraOff,
  onSelect,
  pinned = false,
  avatarSize = 64,
}: {
  participant: CallParticipant
  isSelf: boolean
  bind: (tileId: number, el: HTMLVideoElement) => void
  unbind: (tileId: number) => void
  registerSelfVideo: (el: HTMLVideoElement | null) => void
  selfCameraOff: boolean
  /** Tap-to-pin / unpin this tile (omit to make the tile non-interactive). */
  onSelect?: () => void
  pinned?: boolean
  avatarSize?: number
}) {
  const selfRef = useRef<HTMLVideoElement | null>(null)
  useLayoutEffect(() => {
    if (!isSelf) return
    registerSelfVideo(selfRef.current)
    return () => registerSelfVideo(null)
  }, [isSelf, registerSelfVideo])

  const cameraOn = isSelf ? !selfCameraOff : Boolean(participant.tileId)
  const interactive = Boolean(onSelect)
  return (
    <div
      className={`group relative flex h-full w-full items-center justify-center overflow-hidden rounded-xl bg-slate-800 ${
        participant.speaking ? 'ring-2 ring-emerald-400' : pinned ? 'ring-2 ring-sky-400' : ''
      } ${interactive ? 'cursor-pointer' : ''}`}
      onClick={onSelect}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect?.()
              }
            }
          : undefined
      }
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? pinned : undefined}
      aria-label={
        interactive
          ? `${isSelf ? 'You' : participant.displayName} — ${pinned ? 'unpin tile' : 'pin tile'}`
          : undefined
      }
    >
      {isSelf ? (
        <video
          ref={selfRef}
          className={`absolute inset-0 h-full w-full object-cover ${selfCameraOff ? 'opacity-0' : ''}`}
          autoPlay
          playsInline
          muted
        />
      ) : participant.tileId != null ? (
        <RemoteTileVideo tileId={participant.tileId} bind={bind} unbind={unbind} />
      ) : null}

      {!cameraOn && (
        <AvatarImage
          name={participant.displayName}
          avatarUrl={participant.avatarUrl}
          size={avatarSize}
          variant="provider"
          fallback="logo"
        />
      )}

      {pinned && (
        <span
          className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-sky-500/90 px-2 py-0.5 text-[10px] font-semibold text-white shadow"
          aria-hidden
        >
          <PinIcon className="h-3 w-3" />
          Pinned
        </span>
      )}

      <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-gradient-to-t from-black/70 to-transparent px-2 py-1">
        <span className="truncate text-xs font-medium text-white">
          {isSelf ? 'You' : participant.displayName}
          {participant.role === 'host' ? ' (host)' : ''}
        </span>
        {(isSelf ? false : participant.mutedSelf) && (
          <span className="shrink-0 text-white/80">
            <MicIcon muted />
          </span>
        )}
      </div>
    </div>
  )
}

export function GroupCallOverlay() {
  const session = useVoiceCallSession()
  const { user } = useAuth()
  const {
    requestAcceptCall,
    declineCall,
    hangUp,
    endCallForEveryone,
    addPeople,
    removeParticipant,
    askToMute,
    confirmGroupJoin,
    toggleMute,
    toggleCamera,
    setRemoteAudioElement,
    bindParticipantTile,
    unbindParticipantTile,
    setGroupSelfVideoElement,
    startScreenShare,
    stopScreenShare,
    setContentVideoElement,
  } = useVoiceCall()

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [now, setNow] = useState(Date.now())
  const [showPicker, setShowPicker] = useState(false)
  const [pickerSelected, setPickerSelected] = useState<Set<string>>(new Set())
  const { contacts: directoryContacts, loading: directoryLoading, loadedAt: directoryLoadedAt } =
    useDirectory()
  const [menuFor, setMenuFor] = useState<string | null>(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [sidebarTab, setSidebarTab] = useState<GroupCallSidebarTab>('chat')
  const [showVideoUpgradeNotice, setShowVideoUpgradeNotice] = useState(false)

  const callRowRef = useRef<HTMLDivElement | null>(null)
  const { width: sidebarWidth, dividerProps } = useResizableSidebar(callRowRef, {
    initialWidth: 360,
    minWidth: 280,
    maxWidth: 620,
  })

  const isGroup = Boolean(session?.isGroup)
  const phase = session?.phase
  const active = phase === 'active'
  const canScreenShare = isDesktopWeb()

  const bindAudioRef = useCallback(
    (el: HTMLAudioElement | null) => {
      audioRef.current = el
      if (isGroup) setRemoteAudioElement(el)
    },
    [isGroup, setRemoteAudioElement],
  )

  useLayoutEffect(() => {
    if (!isGroup) return
    setRemoteAudioElement(audioRef.current)
    return () => setRemoteAudioElement(null)
  }, [isGroup, setRemoteAudioElement, session?.callId])

  useEffect(() => {
    if (phase !== 'active' || !session?.connectedAt) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [phase, session?.connectedAt])

  const currentUserId = resolveSelfUserId(user?.userId)
  // In-call chat is owned here (not inside the sidebar panel) so the live
  // subscription stays alive when the chat/panel is toggled closed. Messages are
  // delivered live AND persisted into the conversation thread (when present).
  const chatPanelOpen = sidebarOpen && sidebarTab === 'chat'
  const chat = useInCallChat(session?.callId, currentUserId, session?.conversationId, {
    presenting: Boolean(session?.isGroup && !session.transferRoom),
    joined: phase === 'active',
    isGroup: true,
    panelOpen: chatPanelOpen,
  })
  // Sidebar visibility is per-call: the overlay stays mounted between calls, so
  // without this a new call inherits the previous call's open sidebar
  // (2026-10-07 field bug on the 1:1 overlay: chat auto-opened on the next call).
  const sidebarResetCallId = session?.callId ?? null
  useEffect(() => {
    setSidebarOpen(false)
  }, [sidebarResetCallId])
  const chatAlert = useInCallChatAlert(chat.messages, currentUserId, chatPanelOpen)
  const participants = useMemo(() => session?.participants ?? [], [session?.participants])
  const visibleParticipants = useMemo(
    () => participants.filter((p) => p.state === 'active' || p.state === 'ringing'),
    [participants],
  )
  const liveCount = participants.filter((p) => p.state !== 'left' && p.state !== 'declined').length
  // MUST stay above every early `return` below: this overlay renders `null` for a
  // 1:1 call and full content once it becomes a group. A `useMemo` placed after
  // those returns changes the hook count between renders when a 1:1 is upgraded
  // to a group via "Add people", which threw "rendered more hooks than during the
  // previous render" — crashing the app and dropping the call (esp. on iOS).
  const selfParticipant = useMemo(
    () => participants.find((p) => p.userId === currentUserId),
    [participants, currentUserId],
  )

  const connectStatus = useCallConnectStatus(session)
  const tileView = useTileView(participants, currentUserId)
  // Cap columns on narrow screens so tiles stay tappable in the explicit "Grid"
  // mode; "Auto" keeps the unconstrained best-fit grid.
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false,
  )
  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 640)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  const gridOptions = useMemo(
    () => (tileView.mode === 'grid' ? { maxColumns: isNarrow ? 2 : 3 } : undefined),
    [tileView.mode, isNarrow],
  )
  const { containerRef: gridRef, layout: tileLayout } = useTileGrid(
    visibleParticipants.length,
    gridOptions,
  )
  // Self-view PiP in spotlight layouts (draggable within the stage).
  const spotlightStageRef = useRef<HTMLDivElement | null>(null)
  const { pipStyle: selfPipStyle, pipHandlers: selfPipHandlers } = useDraggablePip(
    spotlightStageRef,
    { defaultInset: { bottom: 16, right: 16 }, pipSize: { width: 120, height: 160 } },
  )

  const isPresenting = Boolean(session?.isPresenting)
  const remotePresenting = Boolean(session?.remotePresenting)
  const presenting = isPresenting || remotePresenting

  const presenterName = useMemo(() => {
    if (isPresenting) return 'You'
    if (!remotePresenting) return null
    const presenter = participants.find(
      (p) => p.state === 'active' && p.userId !== currentUserId,
    )
    return presenter?.displayName ?? 'Someone'
  }, [isPresenting, remotePresenting, participants, currentUserId])

  const resolveName = useCallback(
    (senderId: string) => {
      if (senderId === currentUserId) return 'You'
      return participants.find((p) => p.userId === senderId)?.displayName ?? 'Participant'
    },
    [participants, currentUserId],
  )

  // Surface a screen share the moment it starts: open the panel to the Screen
  // tab so the content <video> mounts and binds, and widen it for visibility.
  const wasPresentingRef = useRef(false)
  useEffect(() => {
    if (presenting && !wasPresentingRef.current) {
      setSidebarOpen(true)
      setSidebarTab('screen')
    }
    wasPresentingRef.current = presenting
  }, [presenting])

  const bindContentVideo = useCallback(
    (el: HTMLVideoElement | null) => setContentVideoElement(el),
    [setContentVideoElement],
  )

  const toggleShare = useCallback(() => {
    if (isPresenting) void stopScreenShare()
    else void startScreenShare()
  }, [isPresenting, startScreenShare, stopScreenShare])

  const handleReAdd = useCallback(
    (participant: CallParticipant) => {
      const user: ChatUser = {
        userId: participant.userId,
        email: '',
        displayName: participant.displayName,
        avatarUrl: participant.avatarUrl,
      }
      void addPeople([user])
    },
    [addPeople],
  )

  // Served from the shared cached directory; recomputed from live participants
  // so the available list stays correct as people join/leave.
  const pickerUsers = useMemo(() => {
    const existing = new Set(
      participants.filter((p) => p.state !== 'left' && p.state !== 'declined').map((p) => p.userId),
    )
    return directoryContacts.filter((u) => u.userId !== currentUserId && !existing.has(u.userId))
  }, [directoryContacts, participants, currentUserId])

  const pickerLoading = directoryLoading || directoryLoadedAt == null

  const openPicker = useCallback(() => {
    setShowPicker(true)
    setPickerSelected(new Set())
    void loadDirectory()
  }, [])

  const confirmAddPeople = useCallback(() => {
    const chosen = pickerUsers.filter((u) => pickerSelected.has(u.userId))
    if (chosen.length > 0) void addPeople(chosen)
    setShowPicker(false)
  }, [addPeople, pickerSelected, pickerUsers])

  // A transferRoom uses the room mechanics under the hood but must render the
  // 1:1 overlay (voice/video) with a transfer status indicator — not the group
  // participant grid — so the group overlay stands down for it.
  if (!session || !session.isGroup || session.transferRoom) return null

  if (session.phase === 'elsewhere') return null

  // iOS: CallKit is presenting this incoming group call — it is the one
  // canonical ring surface, so the in-app ring overlay stands down until the
  // user answers (osRingActive clears / phase leaves incoming).
  if (session.phase === 'incoming' && session.osRingActive) return null

  const isVideo = session.mediaType === 'video'
  const isHost = Boolean(session.isHost)
  const maxParticipants = session.maxParticipants ?? 20
  const atCap = liveCount >= maxParticipants
  const title = session.groupTitle || 'Group call'
  const duration =
    active && session.connectedAt ? formatDuration(now - session.connectedAt) : null

  const selfDisplayName = selfParticipant?.displayName ?? user?.displayName ?? 'You'
  const selfAvatarUrl = selfParticipant?.avatarUrl ?? user?.avatarUrl

  const statusText =
    phase === 'ended'
      ? session.endReason === 'error'
        ? session.errorMessage ?? 'Call failed'
        : 'Call ended'
      : phase === 'incoming'
        ? isVideo
          ? 'Incoming group video call'
          : 'Incoming group call'
        : phase === 'connecting' || phase === 'outgoing'
          ? connectStatus.label
          : `${visibleParticipants.length} in call`

  // Host lobby (Meet-style) before dialing invitees.
  if (phase === 'prejoin' && isVideo) {
    return (
      <GroupCallPrejoin
        mode="host"
        title={title}
        subtitle="Check your camera and microphone, then join when ready."
        participants={participants}
        displayName={selfDisplayName}
        avatarUrl={selfAvatarUrl}
        isMuted={Boolean(session.isMuted)}
        isCameraOff={Boolean(session.isCameraOff)}
        onToggleMute={() => toggleMute()}
        onToggleCamera={() => void toggleCamera()}
        onJoin={() => void confirmGroupJoin()}
        onCancel={() => void hangUp()}
      />
    )
  }

  // Incoming group invite — voice keeps the compact sheet; video gets the lobby.
  if (phase === 'incoming') {
    if (isVideo) {
      return (
        <GroupCallPrejoin
          mode="guest"
          title={title}
          subtitle="Incoming group video call"
          participants={participants}
          displayName={selfDisplayName}
          avatarUrl={selfAvatarUrl}
          isMuted={Boolean(session.isMuted)}
          isCameraOff={Boolean(session.isCameraOff)}
          onToggleMute={() => toggleMute()}
          onToggleCamera={() => void toggleCamera()}
          onJoin={() => requestAcceptCall()}
          onCancel={() => void declineCall()}
          joinLabel="Join now"
          cancelLabel="Decline"
        />
      )
    }

    return (
      <div
        className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-slate-900/70 p-4 backdrop-blur-sm native-modal-top"
        role="dialog"
        aria-modal="true"
        aria-label="Incoming group call"
      >
        <audio ref={bindAudioRef} data-call-remote="true" className="sr-only" aria-hidden playsInline />
        <div className="animate-fade-slide-in w-full max-w-sm rounded-3xl bg-gradient-to-b from-brand-700 to-brand-600 px-6 pb-6 pt-8 text-center text-white shadow-2xl">
          <AvatarImage name={title} size={88} variant="provider" fallback="logo" className="mx-auto ring-4 ring-white/20" />
          <h2 className="mt-4 text-xl font-bold">{title}</h2>
          <p className="mt-1 text-sm text-white/80">{statusText}</p>
          <div className="mt-8 flex items-center justify-center gap-6">
            <button
              type="button"
              onClick={() => void declineCall()}
              className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600"
              aria-label="Decline group call"
            >
              <PhoneIcon className="h-6 w-6 rotate-[135deg]" />
            </button>
            <button
              type="button"
              onClick={() => requestAcceptCall()}
              className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg transition hover:bg-emerald-600"
              aria-label="Accept group call"
            >
              <PhoneIcon className="h-7 w-7" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  const controls = (
    <div className="flex flex-wrap items-center justify-center gap-3">
      <button
        type="button"
        onClick={() => toggleMute()}
        className={`flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition ${
          session.isMuted ? 'bg-white/30 ring-2 ring-white/50' : 'bg-white/10 hover:bg-white/20'
        }`}
        aria-label={session.isMuted ? 'Unmute microphone' : 'Mute microphone'}
        aria-pressed={session.isMuted}
      >
        <MicIcon muted={session.isMuted} />
      </button>

      {isVideo && (
        <button
          type="button"
          onClick={() => void toggleCamera()}
          className={`flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg transition ${
            session.isCameraOff ? 'bg-white/10 hover:bg-white/20' : 'bg-white/30 ring-2 ring-white/50'
          }`}
          aria-label={session.isCameraOff ? 'Turn camera on' : 'Turn camera off'}
          aria-pressed={!session.isCameraOff}
        >
          <CameraIcon off={Boolean(session.isCameraOff)} />
        </button>
      )}

      <CallAudioRouteControl active compact />

      {isVideo && canScreenShare && active && (
        <button
          type="button"
          onClick={toggleShare}
          className={`flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition ${
            isPresenting
              ? 'bg-emerald-400 text-emerald-950 ring-2 ring-emerald-200/80'
              : 'bg-white/10 text-white hover:bg-white/20'
          }`}
          aria-label={isPresenting ? 'Stop sharing your screen' : 'Share your screen'}
          aria-pressed={isPresenting}
          title={isPresenting ? 'Stop sharing your screen' : 'Share your screen'}
        >
          <ScreenShareIcon active={isPresenting} />
        </button>
      )}

      {/* Background effects control temporarily removed — see note at top of file. */}

      <button
        type="button"
        onClick={() => {
          if (chatAlert.hasUnread) {
            setSidebarTab('chat')
            setSidebarOpen(true)
          } else {
            setSidebarOpen((v) => !v)
          }
        }}
        className={`relative flex h-14 w-14 items-center justify-center rounded-full shadow-lg transition ${
          sidebarOpen
            ? 'bg-emerald-400 text-emerald-950 ring-2 ring-emerald-200/80'
            : 'bg-white/10 text-white hover:bg-white/20'
        }`}
        aria-label={
          chatAlert.hasUnread
            ? 'Show chat — new message'
            : sidebarOpen
              ? 'Hide chat and panel'
              : 'Show chat and panel'
        }
        aria-pressed={sidebarOpen}
        title="Chat & participants"
      >
        <ChatIcon />
        {chatAlert.hasUnread && !chatPanelOpen && (
          <span
            className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-red-500 ring-2 ring-slate-950"
            aria-hidden
          />
        )}
      </button>

      {isHost && !isVideo && (
        <button
          type="button"
          onClick={() => setShowVideoUpgradeNotice(true)}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white shadow-lg transition hover:bg-white/20"
          aria-label="Switch group to video"
          title="Switch to video"
        >
          <VideoIcon />
        </button>
      )}

      {isHost && (
        <button
          type="button"
          onClick={() => void openPicker()}
          disabled={atCap}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-white shadow-lg transition hover:bg-white/20 disabled:opacity-40"
          aria-label={atCap ? `Call is full (${maxParticipants})` : 'Add people'}
          title={atCap ? `This call is limited to ${maxParticipants} participants` : 'Add people'}
        >
          <PersonAddIcon />
        </button>
      )}

      <button
        type="button"
        onClick={() => void hangUp()}
        className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600"
        aria-label="Leave call"
      >
        <PhoneIcon className="h-6 w-6 rotate-[135deg]" />
      </button>

      {isHost && active && (
        <button
          type="button"
          onClick={() => void endCallForEveryone()}
          className="rounded-full bg-red-600/80 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-red-700"
        >
          End for all
        </button>
      )}
    </div>
  )

  // Minimized: keep the overlay mounted (audio + tiles keep playing) but
  // hidden; the app-shell ReturnToCallBanner re-presents it on tap/foreground.
  // Keyed on the EXPLICIT user-minimize flag only (not background) so a missed
  // foreground event can't strand the call UI hidden.
  const minimized = Boolean(session.userMinimized)

  return (
    <div
      ref={tileView.fullscreenRef}
      className={`fixed inset-0 z-[60] flex flex-col bg-slate-950 native-modal-top native-modal-bottom ${minimizedCallOverlayClassName(
        minimized,
      )}`}
      data-dark-surface={minimized ? undefined : 'true'}
      role="dialog"
      aria-modal={!minimized}
      aria-hidden={minimized}
      aria-label="Group call"
    >
      <audio ref={bindAudioRef} data-call-remote="true" className="sr-only" aria-hidden playsInline />

      {!minimized && phase !== 'ended' && <MinimizeOverlayButton />}

      <CallReconnectingBanner show={connectStatus.isReconnecting} />

      {session.notice && (
        <div className="pointer-events-none absolute inset-x-0 top-[max(1rem,var(--safe-top))] z-[62] flex justify-center px-4" role="status" aria-live="polite">
          <span className="rounded-full bg-slate-900/85 px-4 py-2 text-sm font-medium text-white shadow-lg">
            {session.notice}
          </span>
        </div>
      )}

      {chatAlert.banner && !chatPanelOpen && (
        <button
          type="button"
          onClick={() => {
            setSidebarTab('chat')
            setSidebarOpen(true)
          }}
          className="absolute inset-x-0 top-[max(3.75rem,calc(var(--safe-top)+3rem))] z-[63] mx-auto flex max-w-sm items-start gap-2 rounded-2xl bg-slate-900/90 px-4 py-3 text-left text-white shadow-xl transition hover:bg-slate-900"
          role="status"
          aria-live="polite"
        >
          <span className="mt-0.5 shrink-0 text-emerald-300">
            <ChatIcon />
          </span>
          <span className="min-w-0">
            <span className="block text-xs font-semibold text-emerald-200">
              {resolveName(chatAlert.banner.senderId)} · in-call message
            </span>
            <span className="block truncate text-sm text-white/90">{chatAlert.banner.text}</span>
          </span>
        </button>
      )}

      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-4 pb-3 pt-3 text-white">
        <div className="min-w-0">
          <h2 className="truncate text-base font-semibold">{title}</h2>
          <p className="text-xs text-white/70" aria-live="polite">
            {duration ? `${duration} · ${statusText}` : statusText}
          </p>
        </div>

        {isVideo && active && (
          <div className="flex shrink-0 items-center gap-2">
            <div
              className="flex items-center rounded-full bg-white/10 p-0.5"
              role="group"
              aria-label="Tile view mode"
            >
              {TILE_VIEW_MODES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => tileView.setMode(m)}
                  className={`rounded-full px-2.5 py-1 text-xs font-semibold transition ${
                    tileView.mode === m
                      ? 'bg-white text-slate-900'
                      : 'text-white/80 hover:text-white'
                  }`}
                  aria-pressed={tileView.mode === m}
                  title={`${TILE_VIEW_LABELS[m]} layout`}
                >
                  {TILE_VIEW_LABELS[m]}
                </button>
              ))}
            </div>
            {tileView.fullscreenSupported && (
              <button
                type="button"
                onClick={tileView.toggleFullscreen}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                aria-label={tileView.isFullscreen ? 'Exit full screen' : 'Enter full screen'}
                aria-pressed={tileView.isFullscreen}
                title={tileView.isFullscreen ? 'Exit full screen' : 'Full screen'}
              >
                <FullscreenIcon active={tileView.isFullscreen} />
              </button>
            )}
          </div>
        )}
      </div>

      <div ref={callRowRef} className="relative flex min-h-0 flex-1 overflow-hidden">
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {isVideo ? (
          tileView.isSpotlight && tileView.focusedUserId ? (
            (() => {
              const focused = visibleParticipants.find(
                (p) => p.userId === tileView.focusedUserId,
              )
              const filmstrip = visibleParticipants.filter(
                (p) => p.userId !== tileView.focusedUserId && p.userId !== currentUserId,
              )
              const showSelfPip =
                Boolean(currentUserId) &&
                tileView.focusedUserId !== currentUserId &&
                visibleParticipants.some((p) => p.userId === currentUserId)
              const selfTile =
                visibleParticipants.find((p) => p.userId === currentUserId) ?? selfParticipant
              return (
                <div
                  ref={spotlightStageRef}
                  className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-3"
                >
                  <div className="relative min-h-0 flex-1">
                    {focused && (
                      <ParticipantTile
                        participant={focused}
                        isSelf={focused.userId === currentUserId}
                        bind={bindParticipantTile}
                        unbind={unbindParticipantTile}
                        registerSelfVideo={setGroupSelfVideoElement}
                        selfCameraOff={Boolean(session.isCameraOff)}
                        onSelect={() => tileView.togglePin(focused.userId)}
                        pinned={tileView.pinnedUserId === focused.userId}
                        avatarSize={96}
                      />
                    )}
                    {showSelfPip && selfTile && (
                      <div
                        {...selfPipHandlers}
                        style={selfPipStyle}
                        className="absolute z-10 h-40 w-28 touch-none"
                      >
                        <ParticipantTile
                          participant={selfTile}
                          isSelf
                          bind={bindParticipantTile}
                          unbind={unbindParticipantTile}
                          registerSelfVideo={setGroupSelfVideoElement}
                          selfCameraOff={Boolean(session.isCameraOff)}
                          avatarSize={36}
                        />
                      </div>
                    )}
                  </div>
                  {filmstrip.length > 0 && (
                    <div className="mt-3 flex shrink-0 gap-2 overflow-x-auto pb-1">
                      {filmstrip.map((p) => (
                        <div key={p.userId} className="aspect-video h-20 shrink-0 sm:h-24">
                          <ParticipantTile
                            participant={p}
                            isSelf={false}
                            bind={bindParticipantTile}
                            unbind={unbindParticipantTile}
                            registerSelfVideo={setGroupSelfVideoElement}
                            selfCameraOff={Boolean(session.isCameraOff)}
                            onSelect={() => tileView.togglePin(p.userId)}
                            pinned={tileView.pinnedUserId === p.userId}
                            avatarSize={32}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })()
          ) : (
            <div
              ref={gridRef}
              className="flex min-h-0 flex-1 items-center justify-center overflow-hidden p-3"
            >
              <div
                className="grid place-content-center"
                style={{
                  gridTemplateColumns: `repeat(${tileLayout.columns}, ${tileLayout.tileWidth}px)`,
                  gridTemplateRows: `repeat(${tileLayout.rows}, ${tileLayout.tileHeight}px)`,
                  gap: 12,
                  maxWidth: '100%',
                  maxHeight: '100%',
                }}
              >
                {visibleParticipants.map((p) => (
                  <div
                    key={p.userId}
                    style={{
                      width: tileLayout.tileWidth,
                      height: tileLayout.tileHeight,
                    }}
                  >
                    <ParticipantTile
                      participant={p}
                      isSelf={p.userId === currentUserId}
                      bind={bindParticipantTile}
                      unbind={unbindParticipantTile}
                      registerSelfVideo={setGroupSelfVideoElement}
                      selfCameraOff={Boolean(session.isCameraOff)}
                      onSelect={() => tileView.togglePin(p.userId)}
                      pinned={tileView.pinnedUserId === p.userId}
                    />
                  </div>
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <ul className="mx-auto flex max-w-md flex-col gap-2">
            {visibleParticipants.map((p) => {
              const isSelf = p.userId === currentUserId
              return (
                <li
                  key={p.userId}
                  className={`flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2 ${
                    p.speaking ? 'ring-2 ring-emerald-400' : ''
                  }`}
                >
                  <AvatarImage name={p.displayName} avatarUrl={p.avatarUrl} size={40} variant="provider" fallback="logo" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">
                      {isSelf ? 'You' : p.displayName}
                      {p.role === 'host' ? ' · host' : ''}
                    </p>
                    <p className="text-xs text-white/60">
                      {p.state === 'ringing' ? 'Ringing…' : p.state === 'active' ? 'In call' : p.state}
                    </p>
                  </div>
                  <span className="shrink-0 text-white/70">
                    {(isSelf ? session.isMuted : p.mutedSelf) ? <MicIcon muted /> : <MicIcon muted={false} />}
                  </span>
                  {isHost && !isSelf && p.state === 'active' && (
                    <div className="relative shrink-0">
                      <button
                        type="button"
                        onClick={() => setMenuFor(menuFor === p.userId ? null : p.userId)}
                        className="rounded-lg px-2 py-1 text-white/70 hover:bg-white/10"
                        aria-label={`Manage ${p.displayName}`}
                      >
                        ⋯
                      </button>
                      {menuFor === p.userId && (
                        <div className="absolute right-0 top-9 z-10 w-36 overflow-hidden rounded-lg bg-white text-sm text-slate-800 shadow-xl">
                          <button
                            type="button"
                            onClick={() => {
                              askToMute(p.userId)
                              setMenuFor(null)
                            }}
                            className="block w-full px-3 py-2 text-left hover:bg-slate-100"
                          >
                            Ask to mute
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              removeParticipant(p.userId)
                              setMenuFor(null)
                            }}
                            className="block w-full px-3 py-2 text-left text-red-600 hover:bg-red-50"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
          </div>
        )}
        {session.videoSendUnavailable && (
          <p className="pointer-events-none absolute inset-x-0 bottom-3 text-center text-xs text-amber-300" role="status">
            Video is full right now
          </p>
        )}
        </div>

        {sidebarOpen && (
          <>
            <div
              {...dividerProps}
              className="hidden w-1.5 shrink-0 cursor-col-resize bg-white/10 transition hover:bg-white/40 focus:bg-white/40 focus:outline-none md:block"
              aria-label="Resize panel"
            />
            <aside
              style={{ width: sidebarWidth }}
              className="z-40 flex min-h-0 shrink-0 flex-col border-l border-white/10 max-md:absolute max-md:inset-0 max-md:!w-full"
            >
              <GroupCallSidebar
                activeTab={sidebarTab}
                onTabChange={setSidebarTab}
                onClose={() => setSidebarOpen(false)}
                chat={chat}
                currentUserId={currentUserId}
                resolveName={resolveName}
                bindContentVideo={bindContentVideo}
                isPresenting={isPresenting}
                remotePresenting={remotePresenting}
                presenterName={presenterName}
                canScreenShare={canScreenShare}
                onToggleShare={toggleShare}
                participants={participants}
                isHost={isHost}
                atCap={atCap}
                liveCount={liveCount}
                maxParticipants={maxParticipants}
                selfMuted={Boolean(session.isMuted)}
                onAddPeople={() => void openPicker()}
                onAskToMute={askToMute}
                onRemove={removeParticipant}
                onReAdd={handleReAdd}
              />
            </aside>
          </>
        )}
      </div>

      <div className="shrink-0 border-t border-white/10 bg-slate-950/90 px-4 py-4">
        {controls}
      </div>

      {showPicker && (
        <div className="absolute inset-0 z-[63] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Add people">
          <div className="native-safe-bottom flex max-h-[80vh] w-full max-w-md flex-col overflow-hidden rounded-t-2xl bg-white sm:rounded-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-900">Add people</h3>
              <button type="button" onClick={() => setShowPicker(false)} className="rounded-lg px-2 py-1 text-slate-500 hover:bg-slate-100" aria-label="Close">
                ✕
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              {pickerLoading ? (
                <p className="p-4 text-sm text-slate-500">Loading…</p>
              ) : pickerUsers.length === 0 ? (
                <p className="p-4 text-sm text-slate-500">No one else to add.</p>
              ) : (
                pickerUsers.map((u) => {
                  const checked = pickerSelected.has(u.userId)
                  const wouldExceed = !checked && liveCount + pickerSelected.size >= maxParticipants
                  return (
                    <label
                      key={u.userId}
                      className={`flex items-center gap-3 border-b border-slate-100 px-4 py-3 ${wouldExceed ? 'opacity-40' : 'cursor-pointer hover:bg-slate-50'}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={wouldExceed}
                        onChange={() => {
                          setPickerSelected((prev) => {
                            const next = new Set(prev)
                            if (next.has(u.userId)) next.delete(u.userId)
                            else next.add(u.userId)
                            return next
                          })
                        }}
                      />
                      <AvatarImage name={u.displayName} avatarUrl={u.avatarUrl} size={36} variant="provider" />
                      <span className="min-w-0 flex-1 truncate text-sm text-slate-800">{u.displayName}</span>
                    </label>
                  )
                })
              )}
            </div>
            <div className="flex items-center justify-between gap-3 border-t border-slate-200 px-4 py-3">
              <span className="text-xs text-slate-500">
                {liveCount + pickerSelected.size}/{maxParticipants}
              </span>
              <button
                type="button"
                onClick={confirmAddPeople}
                disabled={pickerSelected.size === 0}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Add {pickerSelected.size > 0 ? `(${pickerSelected.size})` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      <VideoUpgradeUnavailableDialog
        open={showVideoUpgradeNotice}
        onClose={() => setShowVideoUpgradeNotice(false)}
      />
    </div>
  )
}
